import {
    serverTimestamp,
    doc,
    addDoc,
    setDoc,
    collection,
    getDocs,
    query,
    orderBy,
    deleteDoc,
    increment,
} from 'firebase/firestore';

import { EvaluationShareOptions } from '@labradorsports/constants';
import { calculateEvaluationScore, processEvaluation, safeToDate } from '@labradorsports/utils';

import firestore from '../../firebase-config.js';
import { DataTags } from '../../shared/constants.js';
import { createApiSlice } from '../api.js';
import { CustomMutationDefinition, CustomQueryDefinition } from '../types.js';

function prepRating(evaluationSrc) {
    const logIfBad = () => null;

    return processEvaluation({ ...evaluationSrc }, logIfBad);
}

function prepTemplateSnap(templateSnap) {
    const template = templateSnap.data();
    template.id = templateSnap.id;
    template.createdDate = safeToDate(template.createdDate);
    template.lastModified = safeToDate(template.lastModified);

    return template;
}

function prepTemplateRevisions(revisions: { [key: string]: any }) {
    return Object.fromEntries(
        Object.entries(revisions).map(([key, revision]) => [
            key,
            {
                ...prepRating(revision),
                id: revision.templateId,
            },
        ])
    );
}

export const evaluationsApi = createApiSlice({
    saveEvaluation: {
        type: 'mutation',
        extraOptions: {
            unloggableArg: ['player', 'evaluation'],
        },
        query: ({ activeTeam, evaluation, player, ratingId, template }) => {
            const overallRating = calculateEvaluationScore(
                template.positionWeights,
                player.position,
                evaluation.scores,
                template.scoreScale
            );

            return {
                path: 'teams/saveEvaluation',
                body: {
                    teamId: activeTeam,
                    rosterId: player.id,
                    evaluation: { ...evaluation, overallRating },
                    ratingId: ratingId ?? undefined,
                    templateId: template.id,
                    templateVersion: template.version,
                },
            };
        },
        invalidatesTags: (result, error, { player, ratingId }) => {
            return [
                { type: DataTags.ROSTER_PERSON, id: player.id },
                { type: DataTags.EVALUATION_TEMPLATE_REVISION, id: 'LIST' },
                ratingId
                    ? { type: DataTags.PLAYER_RATING, id: `${player.id}_${ratingId}` }
                    : { type: DataTags.PLAYER_RATING, id: `${player.id}_LIST` },
            ];
        },
    } as CustomMutationDefinition<
        {
            activeTeam: string;
            evaluation: any;
            player: any;
            ratingId?: string;
            template: EvaluationTemplate;
        },
        { id: string }
    >,
    generateEvaluationSummary: {
        type: 'mutation',
        extraOptions: {
            showLoading: false,
            unloggableArg: ['evaluation'],
        },
        query: ({ activeTeam, rosterId, evaluation, evaluationId }) => ({
            path: 'teams/generateEvaluationSummary',
            body: {
                teamId: activeTeam,
                rosterId,
                evaluation,
                evaluationId,
            },
        }),
    } as CustomMutationDefinition<
        {
            activeTeam: string;
            rosterId: string;
            evaluation: any;
            evaluationId?: string;
        },
        any
    >,

    saveEvaluationTemplate: {
        type: 'mutation',
        queryFn: async ({ activeTeam, template, id }, { extra }) => {
            const { logger } = extra;
            const templatesRef = collection(firestore, 'teams', activeTeam, 'evaluationTemplates');
            const { createdDate, lastModified, ...rest } = template;

            let savedId = id;
            if (id) {
                // Update existing template
                const templateDocRef = doc(templatesRef, id);
                await setDoc(
                    templateDocRef,
                    {
                        ...rest,
                        lastModified: serverTimestamp(),
                        version: increment(1),
                    },
                    { merge: true }
                );
            } else {
                // Create new template
                const added = await addDoc(templatesRef, {
                    ...rest,
                    createdDate: serverTimestamp(),
                    lastModified: serverTimestamp(),
                    version: 1,
                });

                savedId = added.id;
            }

            logger.log('saveEvaluationTemplate complete');

            return {
                data: savedId,
            };
        },
        invalidatesTags: () => {
            return [{ type: DataTags.EVALUATION_TEMPLATES, id: 'LIST' }];
        },
    } as CustomMutationDefinition<
        { activeTeam: string; template: Omit<EvaluationTemplate, 'id' | 'version'>; id?: string },
        any
    >,

    deleteEvaluationTemplate: {
        type: 'mutation',
        queryFn: async ({ activeTeam, templateId }, { extra }) => {
            const { logger } = extra;
            await deleteDoc(doc(firestore, 'teams', activeTeam, 'evaluationTemplates', templateId));
            logger.log('deleteEvaluationTemplate complete');
        },
        invalidatesTags: (result, error, arg) => [
            { type: DataTags.EVALUATION_TEMPLATES, id: 'LIST' },
            { type: DataTags.EVALUATION_TEMPLATES, id: arg.templateId },
        ],
    } as CustomMutationDefinition<{ activeTeam: string; templateId: string }, any>,

    getPlayerEvaluations: {
        type: 'query',
        query: ({ activeTeam, playerId }) => ({
            path: 'teams/getPlayerEvaluations',
            query: { teamId: activeTeam, rosterId: playerId },
        }),
        transformResponse: (response: any) => {
            return {
                evaluations: response.evaluations.map(prepRating),
                templateRevisions: prepTemplateRevisions(response.templateRevisions),
            };
        },
        providesTags: (result, error, { playerId }) => {
            const baseTags = [
                { type: DataTags.PLAYER_RATING, id: `${playerId}_LIST` },
                { type: DataTags.EVALUATION_TEMPLATE_REVISION, id: 'LIST' },
            ];

            if (!result) {
                return baseTags;
            }

            const evaluationTags = result.evaluations.map(({ id }) => ({
                type: DataTags.PLAYER_RATING,
                id: `${playerId}_${id}`,
            }));
            const revisionTags = Object.keys(result.templateRevisions).map((id) => ({
                type: DataTags.EVALUATION_TEMPLATE_REVISION,
                id,
            }));

            return [...baseTags, ...evaluationTags, ...revisionTags];
        },
    } as CustomQueryDefinition<{ activeTeam: string; playerId: string }, any>,

    getEvaluationTemplates: {
        type: 'query',
        queryFn: async ({ activeTeam }) => {
            const templatesRef = collection(firestore, 'teams', activeTeam, 'evaluationTemplates');
            const snapshot = await getDocs(query(templatesRef, orderBy('createdDate', 'desc')));

            return {
                data: snapshot.docs.map(prepTemplateSnap),
            };
        },
        providesTags: (templates = []) => [
            { type: DataTags.EVALUATION_TEMPLATES, id: 'LIST' },
            ...templates.map(({ id }) => ({ type: DataTags.EVALUATION_TEMPLATES, id })),
        ],
    } as CustomQueryDefinition<{ activeTeam: string }, any>,

    deleteEvaluation: {
        type: 'mutation',
        query: ({ activeTeam, playerId, evaluationId }) => ({
            path: 'teams/deleteEvaluation',
            body: {
                teamId: activeTeam,
                rosterId: playerId,
                evaluationId,
            },
        }),
        invalidatesTags: (result, error, { playerId, evaluationId }) => [
            { type: DataTags.PLAYER_RATING, id: `${playerId}_LIST` },
            { type: DataTags.PLAYER_RATING, id: `${playerId}_${evaluationId}` },
        ],
    } as CustomMutationDefinition<{ activeTeam: string; playerId: string; evaluationId: string }, any>,

    shareEvaluation: {
        type: 'mutation',
        query: ({ activeTeam, rosterId, evaluationId, shareType }) => ({
            path: 'teams/shareEvaluation',
            body: {
                teamId: activeTeam,
                rosterId,
                evaluationId,
                shareType,
            },
        }),
        invalidatesTags: (result, error, { rosterId, evaluationId }) => [
            { type: DataTags.PLAYER_RATING, id: `${rosterId}_LIST` },
            { type: DataTags.PLAYER_RATING, id: `${rosterId}_${evaluationId}` },
        ],
    } as CustomMutationDefinition<
        {
            activeTeam: string;
            rosterId: string;
            evaluationId: string;
            shareType: keyof typeof EvaluationShareOptions;
        },
        any
    >,

    getMyEvaluations: {
        type: 'query',
        query: () => ({
            path: 'teams/getMyEvaluations',
        }),
        transformResponse: (response: any) => {
            return {
                evaluations: response.evaluations.map(prepRating),
                templateRevisions: prepTemplateRevisions(response.templateRevisions),
            };
        },
        providesTags: (result) => {
            const baseTags = [
                { type: DataTags.PLAYER_RATING, id: 'MY_LIST' },
                { type: DataTags.EVALUATION_TEMPLATE_REVISION, id: 'LIST' },
            ];

            if (!result) {
                return baseTags;
            }

            const evaluationTags = result.evaluations.map(({ id }) => ({
                type: DataTags.PLAYER_RATING,
                id: `MY_${id}`,
            }));
            const revisionTags = Object.keys(result.templateRevisions).map((id) => ({
                type: DataTags.EVALUATION_TEMPLATE_REVISION,
                id,
            }));

            return [...baseTags, ...evaluationTags, ...revisionTags];
        },
    } as CustomQueryDefinition<void, any>,
});

export const {
    useGetPlayerEvaluationsQuery,
    useSaveEvaluationMutation,
    useGenerateEvaluationSummaryMutation,
    useGetEvaluationTemplatesQuery,
    useSaveEvaluationTemplateMutation,
    useDeleteEvaluationTemplateMutation,
    useDeleteEvaluationMutation,
    useShareEvaluationMutation,
    useGetMyEvaluationsQuery,
} = evaluationsApi;
